import { API_BASE_URL, STORAGE_KEYS } from "./constants";
import type { ApiError as ApiErrorType } from "@shared/types";

let accessToken: string | null = null;

export type ApiErrorCategory =
  | "auth"
  | "authorization"
  | "validation"
  | "conflict"
  | "rate_limit"
  | "network"
  | "server"
  | "unavailable"
  | "unknown";

const SAFE_ERROR_MESSAGES: Record<string, string> = {
  AUTH_ERROR: "Invalid email or password.",
  AUTH_EXPIRED: "Your session has expired. Please sign in again.",
  AUTHORIZATION_ERROR: "You do not have permission to perform this action.",
  VALIDATION_ERROR: "Please check the highlighted fields and try again.",
  CONFLICT: "This information is already in use.",
  NOT_FOUND: "The requested item could not be found.",
  RATE_LIMIT_EXCEEDED: "Too many attempts. Please wait before trying again.",
  NETWORK_ERROR:
    "We could not reach Scrubmee. Check your connection and try again.",
  SERVER_ERROR:
    "Scrubmee is temporarily unavailable. Please try again shortly.",
  SERVICE_UNAVAILABLE:
    "Scrubmee is temporarily unavailable. Please try again shortly.",
  MALFORMED_RESPONSE: "We received an unexpected response. Please try again.",
  UNKNOWN_ERROR: "An unexpected error occurred. Please try again.",
};
const UNKNOWN_ERROR_MESSAGE = "An unexpected error occurred. Please try again.";
const SERVER_ERROR_MESSAGE =
  "Scrubmee is temporarily unavailable. Please try again shortly.";
const NETWORK_ERROR_MESSAGE =
  "We could not reach Scrubmee. Check your connection and try again.";
const MALFORMED_RESPONSE_MESSAGE =
  "We received an unexpected response. Please try again.";
const AUTH_EXPIRED_MESSAGE = "Your session has expired. Please sign in again.";

function categoryFor(code: string, statusCode: number): ApiErrorCategory {
  if (code === "NETWORK_ERROR") return "network";
  if (statusCode === 401 || code === "AUTH_ERROR" || code === "AUTH_EXPIRED")
    return "auth";
  if (statusCode === 403 || code === "AUTHORIZATION_ERROR")
    return "authorization";
  if (statusCode === 400 || code === "VALIDATION_ERROR") return "validation";
  if (statusCode === 409 || code === "CONFLICT") return "conflict";
  if (statusCode === 429 || code === "RATE_LIMIT_EXCEEDED") return "rate_limit";
  if (statusCode === 503 || code === "SERVICE_UNAVAILABLE")
    return "unavailable";
  if (statusCode >= 500) return "server";
  return "unknown";
}

function safeMessage(
  code: string,
  statusCode: number,
  backendMessage?: string,
): string {
  if (code === "AUTH_ERROR" && backendMessage?.toLowerCase().includes("locked"))
    return backendMessage;
  if (
    code === "AUTH_ERROR" &&
    backendMessage?.toLowerCase().includes("suspended")
  )
    return backendMessage;
  if (code === "VALIDATION_ERROR" && backendMessage) return backendMessage;
  if (code === "CONFLICT" && backendMessage) return backendMessage;
  return (
    SAFE_ERROR_MESSAGES[code] ??
    (statusCode >= 500 ? SERVER_ERROR_MESSAGE : UNKNOWN_ERROR_MESSAGE)
  );
}

export function getAccessToken(): string | null {
  if (accessToken) return accessToken;
  if (typeof localStorage !== "undefined")
    accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  if (typeof localStorage !== "undefined") {
    if (token) localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
    else localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  }
}

let refreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  if (refreshing && refreshPromise) return refreshPromise;
  refreshing = true;
  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        setAccessToken(null);
        return false;
      }
      const data = (await res.json()) as { access_token: string };
      setAccessToken(data.access_token);
      return true;
    } catch {
      setAccessToken(null);
      return false;
    } finally {
      refreshing = false;
    }
  })();
  return refreshPromise;
}

export class ApiClientError extends Error {
  public category: ApiErrorCategory;
  public requestId?: string;

  constructor(
    public code: string,
    message: string,
    public statusCode: number,
    public details?: Record<string, unknown>,
    requestId?: string,
  ) {
    super(message);
    this.name = "ApiClientError";
    this.category = categoryFor(code, statusCode);
    this.requestId = requestId;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  skipAuth?: boolean;
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, headers = {}, skipAuth = false } = options;
  const h: Record<string, string> = {
    "Content-Type": "application/json",
    ...headers,
  };
  if (!skipAuth) {
    const t = getAccessToken();
    if (t) h["Authorization"] = `Bearer ${t}`;
  }
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: h,
      body: body ? JSON.stringify(body) : undefined,
      credentials: "include",
    });
  } catch {
    throw new ApiClientError("NETWORK_ERROR", NETWORK_ERROR_MESSAGE, 0);
  }
  if (res.status === 401 && !skipAuth) {
    const ok = await refreshAccessToken();
    if (ok) {
      const t = getAccessToken();
      if (t) h["Authorization"] = `Bearer ${t}`;
      try {
        const r2 = await fetch(`${API_BASE_URL}${path}`, {
          method,
          headers: h,
          body: body ? JSON.stringify(body) : undefined,
          credentials: "include",
        });
        return handleRes<T>(r2);
      } catch {
        throw new ApiClientError("NETWORK_ERROR", NETWORK_ERROR_MESSAGE, 0);
      }
    }
    setAccessToken(null);
    if (typeof window !== "undefined") window.location.href = "/login";
    throw new ApiClientError("AUTH_EXPIRED", AUTH_EXPIRED_MESSAGE, 401);
  }
  return handleRes<T>(res);
}

async function handleRes<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new ApiClientError(
      "MALFORMED_RESPONSE",
      MALFORMED_RESPONSE_MESSAGE,
      res.status || 0,
    );
  }
  if (!res.ok) {
    const e = data as ApiErrorType & { error?: { requestId?: string } };
    const code =
      e?.error?.code ?? (res.status >= 500 ? "SERVER_ERROR" : "UNKNOWN_ERROR");
    throw new ApiClientError(
      code,
      safeMessage(code, res.status, e?.error?.message),
      res.status,
      e?.error?.details,
      e?.error?.requestId,
    );
  }
  return data as T;
}

export const api = {
  get: <T>(p: string, o?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(p, { ...o, method: "GET" }),
  post: <T>(p: string, b?: unknown, o?: Omit<RequestOptions, "method">) =>
    request<T>(p, { ...o, method: "POST", body: b }),
  put: <T>(p: string, b?: unknown, o?: Omit<RequestOptions, "method">) =>
    request<T>(p, { ...o, method: "PUT", body: b }),
  delete: <T>(p: string, o?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(p, { ...o, method: "DELETE" }),
};

export type UploadPurpose =
  | "kyc_document"
  | "property_image"
  | "avatar"
  | "job_evidence"
  | "dispute_evidence";

/**
 * Uploads a file through the presign flow and returns the public URL.
 */
export async function uploadFile(
  file: File,
  purpose: UploadPurpose,
): Promise<string> {
  const presign = await request<{
    data: { object_key: string; upload_url: string; max_size: number };
  }>(`/uploads/presign`, {
    method: "POST",
    body: {
      filename: file.name,
      content_type: file.type || "application/octet-stream",
      purpose,
    },
  });
  const { object_key, upload_url } = presign.data;
  const token = getAccessToken();
  const res = await fetch(`${API_BASE_URL}${upload_url}`, {
    method: "PUT",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: file,
    credentials: "include",
  });
  if (!res.ok) {
    throw new ApiClientError(
      "SERVER_ERROR",
      "Could not upload the file. Please try again.",
      res.status,
    );
  }
  return `/api/uploads/${object_key}`;
}
